import { useEffect, useState } from 'react';
import { useAuth } from '@chainlit/react-client';
import { Navigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Loader2, Building2, BookOpen } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';

import { orgApi, type Org, type OrgKB } from '@/api/organizations';

export default function OrganizationsPage() {
  const { user: me } = useAuth();
  const myRole = (me?.metadata as Record<string, unknown>)?.role as string | undefined;

  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Org | null>(null);
  const [editName, setEditName] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Org | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // KB assignment dialog
  const [kbOpen, setKbOpen] = useState(false);
  const [kbTarget, setKbTarget] = useState<Org | null>(null);
  const [kbList, setKbList] = useState<OrgKB[]>([]);
  const [kbLoading, setKbLoading] = useState(false);
  const [kbSaving, setKbSaving] = useState(false);
  const [kbError, setKbError] = useState('');

  if (myRole !== 'super_admin') return <Navigate replace to="/chat" />;

  const fetchOrgs = () => {
    setLoading(true);
    orgApi.list()
      .then(setOrgs)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchOrgs(); }, []);

  // ── Create ──
  const handleCreate = async () => {
    setCreateError('');
    if (!createName.trim()) { setCreateError('業者名稱不可為空'); return; }
    setCreateLoading(true);
    try {
      const org = await orgApi.create(createName.trim());
      setOrgs((prev) => [...prev, org]);
      setCreateOpen(false);
      setCreateName('');
    } catch (e: unknown) {
      setCreateError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Edit ──
  const openEdit = (org: Org) => {
    setEditTarget(org);
    setEditName(org.name);
    setEditError('');
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    setEditError('');
    if (!editName.trim()) { setEditError('業者名稱不可為空'); return; }
    setEditLoading(true);
    try {
      await orgApi.update(editTarget.id, editName.trim());
      setOrgs((prev) => prev.map((o) => o.id === editTarget.id ? { ...o, name: editName.trim() } : o));
      setEditOpen(false);
    } catch (e: unknown) {
      setEditError(e instanceof Error ? e.message : String(e));
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await orgApi.delete(deleteTarget.id);
      setOrgs((prev) => prev.filter((o) => o.id !== deleteTarget.id));
      setDeleteOpen(false);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setDeleteOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── KB Assignment ──
  const openKbDialog = async (org: Org) => {
    setKbTarget(org);
    setKbError('');
    setKbOpen(true);
    setKbLoading(true);
    try {
      const list = await orgApi.getKbs(org.id);
      setKbList(list);
    } catch (e: unknown) {
      setKbError(e instanceof Error ? e.message : String(e));
    } finally {
      setKbLoading(false);
    }
  };

  const toggleKb = (kbId: string) => {
    setKbList((prev) => prev.map((kb) => kb.id === kbId ? { ...kb, assigned: !kb.assigned } : kb));
  };

  const handleSaveKbs = async () => {
    if (!kbTarget) return;
    setKbSaving(true);
    setKbError('');
    try {
      const assigned = kbList.filter((kb) => kb.assigned).map((kb) => kb.id);
      await orgApi.setKbs(kbTarget.id, assigned);
      setKbOpen(false);
    } catch (e: unknown) {
      setKbError(e instanceof Error ? e.message : String(e));
    } finally {
      setKbSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">業者管理</h2>
        <Button
          variant="outline"
          className="border-primary text-primary hover:bg-primary/10"
          onClick={() => { setCreateError(''); setCreateName(''); setCreateOpen(true); }}
        >
          <Plus className="size-4 mr-1" />
          新增業者
        </Button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
          <Loader2 className="size-5 animate-spin" /><span>載入中…</span>
        </div>
      )}
      {!loading && error && <div className="text-center py-8 text-red-500 text-sm">{error}</div>}

      {!loading && !error && (
        orgs.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Building2 className="size-12 mx-auto mb-3 opacity-40" />
            <p>尚無業者，點擊右上角新增</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg shadow-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>業者名稱</TableHead>
                  <TableHead>用戶數</TableHead>
                  <TableHead>建立時間</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgs.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell className="text-muted-foreground">{org.user_count}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(org.created_at).toLocaleString('zh-TW')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" title="指派知識庫" onClick={() => openKbDialog(org)}>
                          <BookOpen className="size-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(org)}>
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => { setDeleteTarget(org); setDeleteOpen(true); }}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>新增業者</DialogTitle>
            <DialogDescription>建立新的業者（公司行號）</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-sm font-medium">業者名稱</label>
            <Input value={createName} onChange={(e) => setCreateName(e.target.value)} placeholder="輸入業者名稱" />
            {createError && <p className="text-sm text-red-500">{createError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>取消</Button>
            <Button onClick={handleCreate} disabled={createLoading}>
              {createLoading && <Loader2 className="size-4 mr-1 animate-spin" />}建立
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>編輯業者</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="text-sm font-medium">業者名稱</label>
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
            {editError && <p className="text-sm text-red-500">{editError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>取消</Button>
            <Button onClick={handleEdit} disabled={editLoading}>
              {editLoading && <Loader2 className="size-4 mr-1 animate-spin" />}儲存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確認刪除</AlertDialogTitle>
            <AlertDialogDescription>
              確定要刪除業者「{deleteTarget?.name}」嗎？該業者的所有用戶將失去業者綁定，此操作無法復原。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteLoading} className="bg-red-600 hover:bg-red-700">
              {deleteLoading && <Loader2 className="size-4 mr-1 animate-spin" />}刪除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* KB Assignment Dialog */}
      <Dialog open={kbOpen} onOpenChange={setKbOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>指派知識庫 — {kbTarget?.name}</DialogTitle>
            <DialogDescription>勾選此業者可存取的知識庫</DialogDescription>
          </DialogHeader>
          <div className="py-2 max-h-72 overflow-y-auto space-y-2">
            {kbLoading && (
              <div className="flex items-center gap-2 text-muted-foreground py-4 justify-center">
                <Loader2 className="size-4 animate-spin" /><span>載入中…</span>
              </div>
            )}
            {!kbLoading && kbList.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">尚無知識庫</p>
            )}
            {!kbLoading && kbList.map((kb) => (
              <label key={kb.id} className="flex items-start gap-3 p-2 rounded hover:bg-muted cursor-pointer">
                <Checkbox
                  checked={kb.assigned}
                  onCheckedChange={() => toggleKb(kb.id)}
                  className="mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium">{kb.name}</p>
                  {kb.description && <p className="text-xs text-muted-foreground">{kb.description}</p>}
                </div>
              </label>
            ))}
            {kbError && <p className="text-sm text-red-500">{kbError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKbOpen(false)}>取消</Button>
            <Button onClick={handleSaveKbs} disabled={kbSaving || kbLoading}>
              {kbSaving && <Loader2 className="size-4 mr-1 animate-spin" />}儲存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
